import { Text } from '@timelyapp/react';
import React from 'react';
import type { LinkedWorkItem } from 'redux/modules/superreports-datatable.types';
import { safeHttpHref } from 'utils/url-helpers';
import styles from './column.module.scss';
import { COLUMN_SIZE_MEDIUM } from './constants';
import HeaderWithMenu from './HeaderWithMenu';
import type { DatatableCellRenderer, DatatableColumnGetter } from './types';

type LinkedWorkField = 'external_code' | 'title' | 'status' | 'jira_custom_field';

export const getLinkedWorkColumn: DatatableColumnGetter<
  LinkedWorkItem[] | undefined,
  { label: string; field: LinkedWorkField; index?: number; parentColumnId?: string }
> = (
  id: string,
  { label, field, index, parentColumnId } = { label: '', field: 'external_code' }
) => ({
  id,
  header: props => <HeaderWithMenu {...props} label={label} sortLabelType="string" />,
  minSize: COLUMN_SIZE_MEDIUM,
  enableResizing: true,
  enableSorting: false,
  meta: {
    numeric: false,
    mandatory: false,
    label: label,
    parentColumnId: parentColumnId,
    linkedWork: true,
    linkedWorkField: field,
    linkedWorkIndex: index,
    labelPlural:
      field === 'external_code'
        ? 'IDs'
        : field === 'title'
          ? 'Titles'
          : field === 'jira_custom_field'
            ? 'Jira custom fields'
            : 'Sync Statuses',
  },
  cell: props => <LinkedWorkCell {...props} field={field} index={index} />,
});

/**
 * An item's code, linked to the item when it carries a URL that is safe to open. `html_url` comes
 * from the integration or the public API, so anything but an absolute http(s) URL renders as plain
 * text. The full title rides along as a tooltip, since the cell shows only the code.
 */
const LinkedWorkCode = ({ item }: { item: LinkedWorkItem }) => {
  const href = safeHttpHref(item.html_url);
  if (href == null) {
    return <span title={item.title || undefined}>{item.external_code}</span>;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.linkedWorkLink}
      title={item.title || undefined}
      onClick={e => e.stopPropagation()}
    >
      {item.external_code}
    </a>
  );
};

export const LinkedWorkCell: DatatableCellRenderer<
  LinkedWorkItem[] | undefined,
  { field: LinkedWorkField; index?: number }
> = ({ getValue, field, index }) => {
  const linkedWorkItems = getValue();

  if (!linkedWorkItems || linkedWorkItems.length === 0) {
    return null;
  }

  // If index is specified, show only that specific item
  if (index !== undefined) {
    const item = linkedWorkItems[index];

    if (!item) {
      return null;
    }

    // For external_code, show as clickable link if html_url exists
    if (field === 'external_code' && item.external_code) {
      return (
        <Text size="small" className="fs-mask">
          <LinkedWorkCode item={item} />
        </Text>
      );
    }

    // For title and status, show the value
    const value = item[field] || '';
    // Long values are clipped by the cell; the tooltip carries the full text.
    return (
      <Text size="small" className="fs-mask" title={value || undefined}>
        {value}
      </Text>
    );
  }

  // Legacy: If no index specified, show all items comma-separated
  // For external_code, show all codes as clickable links if html_url exists
  if (field === 'external_code') {
    const items = linkedWorkItems.filter(item => item.external_code);

    if (items.length === 0) {
      return null;
    }

    return (
      <Text size="small" className="fs-mask">
        {items.map((item, idx) => (
          <React.Fragment key={item.uri}>
            {idx > 0 && ', '}
            <LinkedWorkCode item={item} />
          </React.Fragment>
        ))}
      </Text>
    );
  }

  // For title and status, show all items comma-separated
  const values = linkedWorkItems
    .map(item => item[field])
    .filter(value => value && value.trim() !== '');

  if (values.length === 0) {
    return null;
  }

  return (
    <Text size="small" className="fs-mask" title={values.join(', ')}>
      {values.join(', ')}
    </Text>
  );
};
